import axios, { AxiosError } from "axios";
import { env } from "@/config";
import {
  AssistedAffiliationValidationItem,
  AssistedAffiliationValidationName,
  AssistedAffiliationValidationResponse,
  RncValidationFeedback,
} from "@/screens/AssistedAffiliationRnc/types";
import {
  DEFAULT_LEGAL_NAME,
  RNC_VALIDATION_SUCCESS_LINES,
} from "@/screens/AssistedAffiliationRnc/constants";

const validateIdentificationEndpoint =
  "/v1/assisted-affiliation/validate";

const validationNames: AssistedAffiliationValidationName[] = [
  "dgii_validation",
  "billet_validation",
  "cardnet_validation",
  "request_validation",
];

const failedValidationCopy: Record<
  AssistedAffiliationValidationName,
  {
    incident: string;
    kind: RncValidationFeedback["kind"];
    tone: RncValidationFeedback["tone"];
  }
> = {
  dgii_validation: {
    incident: "RNC no activo ante DGII",
    kind: "inactiveInDgii",
    tone: "error",
  },
  billet_validation: {
    incident: "Comercio existente en Billet",
    kind: "existsInBillet",
    tone: "warning",
  },
  cardnet_validation: {
    incident: "Comercio existente en Cardnet",
    kind: "existsInCardnet",
    tone: "warning",
  },
  request_validation: {
    incident: "Solicitud en curso",
    kind: "duplicatedRequest",
    tone: "warning",
  },
};

const buildValidateIdentificationPayload = (documentNumber: string) => ({
  validations: validationNames.map((name) => ({
    name,
    value: documentNumber.trim(),
  })),
});

const isFailedValidation = (validation: AssistedAffiliationValidationItem) =>
  validation.code.endsWith("_002");

const getFailedValidations = (
  response: AssistedAffiliationValidationResponse
) => response.data?.validations.filter(isFailedValidation) || [];

const buildSingleFailureFeedback = (
  validation: AssistedAffiliationValidationItem
): RncValidationFeedback => {
  const copy = failedValidationCopy[validation.name];

  if (validation.name === "billet_validation") {
    return {
      kind: copy.kind,
      tone: copy.tone,
      description: validation.message,
      commercialName: validation.commercial_name || DEFAULT_LEGAL_NAME,
    };
  }

  if (validation.name === "cardnet_validation") {
    return {
      kind: copy.kind,
      tone: copy.tone,
      description: validation.message,
      mid: validation.mid,
    };
  }

  if (validation.name === "dgii_validation") {
    return {
      kind: copy.kind,
      tone: copy.tone,
      description: validation.message,
      foundStatus: validation.found_status || "Suspendido",
    };
  }

  return {
    kind: copy.kind,
    tone: copy.tone,
    description: validation.message,
    registryNumber: validation.registry_number,
    status: validation.status,
  };
};

const buildMultipleIncidentsFeedback = (
  validations: AssistedAffiliationValidationItem[]
): RncValidationFeedback => ({
  kind: "multipleIncidents",
  tone: "error",
  title: "Se encontraron incidencias:",
  incidents: validations
    .map((validation) => failedValidationCopy[validation.name]?.incident)
    .filter(Boolean),
});

const mapValidationResponseToFeedback = (
  response: AssistedAffiliationValidationResponse
): RncValidationFeedback => {
  const failedValidations = getFailedValidations(response);

  if (failedValidations.length === 0) {
    return {
      kind: "success",
      tone: "success",
      lines: RNC_VALIDATION_SUCCESS_LINES,
    };
  }

  if (response.code === "MI-001" || failedValidations.length > 1) {
    return buildMultipleIncidentsFeedback(failedValidations);
  }

  const [failedValidation] = failedValidations;
  return buildSingleFailureFeedback(failedValidation);
};

export const validateIdentificationService = async (
  documentNumber: string
): Promise<RncValidationFeedback> => {
  try {
    const response = await axios.post<AssistedAffiliationValidationResponse>(
      `${env.MOCK_API_URL}${validateIdentificationEndpoint}`,
      buildValidateIdentificationPayload(documentNumber),
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    return mapValidationResponseToFeedback(response.data);
  } catch (error) {
    const axiosError =
      error as AxiosError<AssistedAffiliationValidationResponse>;

    if (axiosError.response?.data?.data?.validations) {
      return mapValidationResponseToFeedback(axiosError.response.data);
    }

    throw error;
  }
};

export const getValidateIdentificationErrorMessage = (error: unknown) => {
  const axiosError =
    error as AxiosError<AssistedAffiliationValidationResponse | string>;
  const responseData = axiosError.response?.data;

  if (typeof responseData === "string") {
    return responseData;
  }

  return (
    responseData?.message ||
    axiosError.message ||
    "No se pudo validar la identificación del comercio."
  );
};
